import React, { useEffect, useMemo } from 'react';
import { AutoComplete, Card, Form, Input, Modal, Space, message, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined, LeftOutlined } from '@ant-design/icons';
import type { QaqcPipeHeatno } from '../../../../types';
import { insertData, updateData } from '../../../../services/api';
import { useGetQaqcPipeHeatno } from '../../../../hooks/genericHooks';
import Button from '../../../../components/common/Button';

export type QaqcPipeHeatnoMode = 'create' | 'edit';

interface QaqcPipeHeatnoCreateProps {
  mode?: QaqcPipeHeatnoMode;
  initialValues?: Partial<QaqcPipeHeatno> | null;
  onSaved?: (saved: QaqcPipeHeatno | any) => void;
  onCancel?: () => void;
  onBack?: () => void;
  viewOnly?: boolean;
}

type FormValues = {
  project_id?: number | string;
  component_name?: string;
  heat_no?: string;
  manufacture?: string;
  pmi_ref_no?: string;
  lines?: Array<{
    component_name?: string;
    heat_no?: string;
    manufacture?: string;
    pmi_ref_no?: string;
  }>;
};

const QaqcPipeHeatnoCreate: React.FC<QaqcPipeHeatnoCreateProps> = ({
  mode = 'create',
  initialValues,
  onSaved,
  onCancel,
  onBack,
  viewOnly,
}) => {
  const [form] = Form.useForm<FormValues>();
  const isEdit = mode === 'edit';
  const isViewOnly = !!viewOnly;
  const { data: heatResp } = useGetQaqcPipeHeatno();
  const compSet = useMemo(() => {
    const rows: QaqcPipeHeatno[] = (heatResp?.data as any) || [];
    const set = new Set<string>();
    rows.forEach(r => { const v = (r.component_name || '').trim(); if (v) set.add(v); });
    return set;
  }, [heatResp]);
  const compOptions = useMemo(() => Array.from(compSet).sort().map(v => ({ value: v })), [compSet]);

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        project_id: initialValues.project_id ?? 1,
        component_name: initialValues.component_name ?? undefined,
        heat_no: initialValues.heat_no ?? undefined,
        manufacture: initialValues.manufacture ?? undefined,
        pmi_ref_no: initialValues.pmi_ref_no ?? undefined,
      } as any);
    } else {
      form.setFieldsValue({ project_id: 1 } as any);
    }
  }, [initialValues, form]);

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const comp = String(values.component_name || '').trim();
      const isNewComp = !!comp && !compSet.has(comp);
      if (isNewComp) {
        const confirmed: boolean = await new Promise((resolve) => {
          Modal.confirm({
            title: 'Use a new component name?',
            content: `You entered a new component name: "${comp}". Do you want to proceed?`,
            okText: 'Yes, use it',
            cancelText: 'No',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!confirmed) return;
      }
      const payload: any = {
        // project_id omitted per request
        component_name: comp || null,
        heat_no: values.heat_no || null,
        manufacture: values.manufacture || null,
        pmi_ref_no: values.pmi_ref_no || null,
      };

      if (isEdit && initialValues?.id) {
        await updateData('qaqc_pipe_heatno', initialValues.id, payload, 'id');
        message.success('Heat number updated');
        try { window.dispatchEvent(new Event('qaqc_pipe_heatno:changed')); } catch {}
        onSaved?.({ id: initialValues.id, ...payload });
      } else {
        const resp = await insertData('qaqc_pipe_heatno', payload);
        message.success('Heat number created');
        try { window.dispatchEvent(new Event('qaqc_pipe_heatno:changed')); } catch {}
        onSaved?.({ id: resp?.ids?.[0], ...payload });
        form.resetFields();
      }
    } catch (e: any) {
      if (e?.errorFields) return;
      const msg = e?.response?.data?.error || e?.message || 'Save failed';
      message.error(msg);
    }
  };

  // Batch submit for create mode using Form.List lines
  const onSubmitBatch = async () => {
    try {
      const v = await form.validateFields();
      const lines = (v?.lines || []) as Array<Partial<QaqcPipeHeatno>>;
      const cleaned = lines
        .map((x) => ({
          component_name: (x.component_name || '').trim() || null,
          heat_no: (x.heat_no || '').trim() || null,
          manufacture: (x.manufacture || '').trim() || null,
          pmi_ref_no: (x.pmi_ref_no || '').trim() || null,
        }))
        .filter((x) => !!x.component_name && !!x.heat_no);
      if (cleaned.length === 0) { message.warning('Add at least one valid line'); return; }

      // Optional: warn for brand-new component names (not in compSet). For brevity, skip modal confirmations in batch.
      await insertData('qaqc_pipe_heatno', cleaned);
      message.success(`Inserted ${cleaned.length} heat no record(s)`);
      try { window.dispatchEvent(new Event('qaqc_pipe_heatno:changed')); } catch {}
      form.resetFields();
      onSaved?.(cleaned);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.error || e?.message || 'Save failed');
    }
  };

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 18 }}>
            {(isEdit || isViewOnly) && onBack && (
              <a onClick={onBack} style={{ marginRight: 16, fontSize: 16 }}>&larr; Back</a>
            )}
            {isViewOnly ? 'View Heat Number' : isEdit ? 'Edit Heat Number' : 'Create Heat Numbers'}
          </span>
        </div>
      }
      bordered
      size="small"
    >
      {/* Edit/View keeps single-record form. Create mode shows batch (+ADD) list. */}
      {isEdit || isViewOnly ? (
        <Form form={form} layout="vertical" disabled={isViewOnly}>
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            <Space style={{ gap: 12, display: 'flex', flexWrap: 'wrap' }}>
              <Form.Item label="Component" name="component_name" rules={[{ required: true, max: 10 }]} style={{ marginBottom: 0 }}>
                <AutoComplete
                  options={compOptions}
                  placeholder="Select or type component"
                  style={{ width: 200 }}
                  disabled={isViewOnly || isEdit}
                  filterOption={(inputValue, option) =>
                    String(option?.value || '')
                      .toLowerCase()
                      .includes(String(inputValue || '').toLowerCase())
                  }
                />
              </Form.Item>
              <Form.Item label="Heat No" name="heat_no" rules={[{ required: true, max: 20 }]} style={{ marginBottom: 0 }}>
                <Input maxLength={20} style={{ width: 220 }} />
              </Form.Item>
              <Form.Item label="Manufacture" name="manufacture" rules={[{ max: 10 }]} style={{ marginBottom: 0 }}>
                <Input maxLength={10} style={{ width: 200 }} />
              </Form.Item>
              <Form.Item label="PMI Ref No" name="pmi_ref_no" rules={[{ max: 20 }]} style={{ marginBottom: 0 }}>
                <Input maxLength={20} style={{ width: 220 }} />
              </Form.Item>
            </Space>

            {!isViewOnly && (
              <Space>
                <Button action="edit" type="primary" onClick={onSubmit}>Update</Button>
                {onCancel && <Button onClick={onCancel}>Cancel</Button>}
              </Space>
            )}
          </Space>
        </Form>
      ) : (
        <Form form={form} layout="vertical">
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            Add multiple heat numbers using +ADD, then Save All. Project number is not required.
          </Typography.Paragraph>
          <Form.List name="lines">
            {(fields, { add, remove }) => (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div>
                  <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()}>
                    Add Line
                  </Button>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', width: 220 }}>Component</th>
                        <th style={{ textAlign: 'left', width: 200 }}>Heat No</th>
                        <th style={{ textAlign: 'left', width: 160 }}>Manufacture</th>
                        <th style={{ textAlign: 'left', width: 200 }}>PMI Ref No</th>
                        <th style={{ width: 80 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {fields.length === 0 ? (
                        <tr>
                          <td colSpan={5}>
                            <Typography.Text type="secondary">No lines yet. Click Add Line to start.</Typography.Text>
                          </td>
                        </tr>
                      ) : (
                        fields.map((field) => (
                          <tr key={field.key}>
                            <td>
                              <Form.Item name={[field.name, 'component_name']} rules={[{ required: true, message: 'Component required' }, { max: 10 }]} style={{ margin: 0 }}>
                                <AutoComplete
                                  options={compOptions}
                                  placeholder="Component"
                                  style={{ width: 200 }}
                                  filterOption={(inputValue, option) =>
                                    String(option?.value || '')
                                      .toLowerCase()
                                      .includes(String(inputValue || '').toLowerCase())
                                  }
                                />
                              </Form.Item>
                            </td>
                            <td>
                              <Form.Item name={[field.name, 'heat_no']} rules={[{ required: true, message: 'Heat no required' }, { max: 20 }]} style={{ margin: 0 }}>
                                <Input maxLength={20} placeholder="Heat No" style={{ width: 180 }} />
                              </Form.Item>
                            </td>
                            <td>
                              <Form.Item name={[field.name, 'manufacture']} rules={[{ max: 10 }]} style={{ margin: 0 }}>
                                <Input maxLength={10} placeholder="Manufacture" style={{ width: 150 }} />
                              </Form.Item>
                            </td>
                            <td>
                              <Form.Item name={[field.name, 'pmi_ref_no']} rules={[{ max: 20 }]} style={{ margin: 0 }}>
                                <Input maxLength={20} placeholder="PMI Ref No" style={{ width: 180 }} />
                              </Form.Item>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <Button danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} size="small" />
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <div>
                  <Space>
                    <Button action="add" type="primary" onClick={onSubmitBatch}>Save All</Button>
                    {onCancel && <Button onClick={onCancel}>Cancel</Button>}
                  </Space>
                </div>
              </div>
            )}
          </Form.List>
        </Form>
      )}
    </Card>
  );
};

export default QaqcPipeHeatnoCreate;
